/**
 * AIShield Milestone 3 Stage B: Filtering, Stable Sorting, and Pagination
 */

import type { RequestRecord, FilterState } from "../types/telemetry.ts";
import { INSPECTION_FAILURE_STATUSES } from "./aggregation.ts";

/**
 * Applies search query, sidebar shortcuts, decision, and inspection status filters.
 */
export function filterRequests(
  requests: RequestRecord[],
  filterState: Pick<FilterState, "shortcut" | "searchQuery" | "decisionFilter" | "inspectionFilter">
): RequestRecord[] {
  const { shortcut, searchQuery, decisionFilter, inspectionFilter } = filterState;
  const query = searchQuery.trim().toLowerCase();

  return requests.filter((req) => {
    // 1. Search Query (Path, IP, client_request_id, UUID)
    if (query) {
      const matchPath = req.raw_path.toLowerCase().includes(query);
      const matchIp = req.client_ip.toLowerCase().includes(query);
      const matchId = req.id.toLowerCase().includes(query);
      const matchClientId = req.client_request_id
        ? req.client_request_id.toLowerCase().includes(query)
        : false;

      if (!matchPath && !matchIp && !matchId && !matchClientId) {
        return false;
      }
    }

    // 2. Sidebar Shortcuts
    if (shortcut === "blocked" && req.policy_decision !== "BLOCK") {
      return false;
    }
    if (shortcut === "flagged" && req.policy_decision !== "FLAG") {
      return false;
    }
    if (shortcut === "failures") {
      // Must be an actual inspection failure (one of the 7 failure statuses)
      if (!INSPECTION_FAILURE_STATUSES.has(req.inspection_status)) {
        return false;
      }
    }

    // 3. Decision Dropdown Filter
    if (decisionFilter !== "ALL") {
      if (decisionFilter === "NOT_EVALUATED") {
        if (req.policy_decision !== null) return false;
      } else {
        if (req.policy_decision !== decisionFilter) return false;
      }
    }

    // 4. Inspection Status Dropdown Filter
    if (inspectionFilter !== "ALL") {
      if (req.inspection_status !== inspectionFilter) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Stable sort with null-score distinction (nulls always sort last) and deterministic ID tie-breaker.
 */
export function sortRequests(
  requests: RequestRecord[],
  sortField: FilterState["sortField"],
  sortOrder: FilterState["sortOrder"]
): RequestRecord[] {
  const multiplier = sortOrder === "asc" ? 1 : -1;

  return [...requests].sort((a, b) => {
    // 1. Primary Field Comparison
    let primaryDiff = 0;

    switch (sortField) {
      case "timestamp": {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        primaryDiff = (timeA - timeB) * multiplier;
        break;
      }
      case "method": {
        primaryDiff = a.method.localeCompare(b.method) * multiplier;
        break;
      }
      case "raw_path": {
        primaryDiff = a.raw_path.localeCompare(b.raw_path) * multiplier;
        break;
      }
      case "response_status": {
        primaryDiff = (a.response_status - b.response_status) * multiplier;
        break;
      }
      case "latency_ms": {
        primaryDiff = (a.latency_ms - b.latency_ms) * multiplier;
        break;
      }
      case "policy_decision": {
        const decA = a.policy_decision ?? "NOT_EVALUATED";
        const decB = b.policy_decision ?? "NOT_EVALUATED";
        primaryDiff = decA.localeCompare(decB) * multiplier;
        break;
      }
      case "inspection_status": {
        primaryDiff = a.inspection_status.localeCompare(b.inspection_status) * multiplier;
        break;
      }
      case "blocking_score": {
        // Null scores are distinct from 0 and MUST sort last regardless of order
        if (a.blocking_score === null && b.blocking_score === null) {
          primaryDiff = 0;
        } else if (a.blocking_score === null) {
          return 1; // a is null, goes to end
        } else if (b.blocking_score === null) {
          return -1; // b is null, goes to end
        } else {
          primaryDiff = (a.blocking_score - b.blocking_score) * multiplier;
        }
        break;
      }
      default:
        primaryDiff = 0;
    }

    if (primaryDiff !== 0) {
      return primaryDiff;
    }

    // 2. Deterministic Tie-Breaker: id DESC
    return b.id.localeCompare(a.id);
  });
}

/**
 * Paginates requests into page slices.
 */
export function paginateRequests(
  requests: RequestRecord[],
  page: number,
  pageSize: number
): {
  items: RequestRecord[];
  totalPages: number;
  startIndex: number;
  endIndex: number;
} {
  const total = requests.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const validPage = Math.min(Math.max(1, page), totalPages);

  const startIndex = (validPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);
  const items = requests.slice(startIndex, endIndex);

  return {
    items,
    totalPages,
    startIndex,
    endIndex,
  };
}
